import { useEffect, useState } from 'react';
import { InfoBanner } from '@vinted/web-ui';
import { useSelector } from 'react-redux';
import { TaxpayerBannerTrackingId, TaxpayerBannerType } from 'constants/taxpayer';
import { dismissTaxpayerRestrictionBanner, dismissTaxpayerRestrictionModal } from 'data/api';
import { navigateToPage } from 'libs/utils/window';
import useTracking from 'hooks/useTracking';
import { clickEvent, viewEvent } from 'libs/common/event-tracker/events';
import { ViewableElement } from 'constants/tracking/viewable-elements';
import { getScreenName } from 'state/screen/selectors';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import TaxpayerRestrictionInfoModal from 'components/Taxpayer/TaxpayerRestrictionInfoModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const TaxpayerBanner = _ref => {
  let {
    banner,
    isBannerInFeed,
    screen
  } = _ref;
  const {
    track
  } = useTracking();
  const {
    0: show,
    1: setShow
  } = useState(true);
  const {
    0: showModal,
    1: setShowModal
  } = useState(true);
  const screenName = useSelector(getScreenName);
  useEffect(() => {
    if (!banner.id) return;
    if (isBannerInFeed && banner.newsFeed.dismissed) return;
    track(viewEvent({
      target: ViewableElement.TaxpayersDac7Banner,
      targetDetails: banner.id,
      screen: screen || screenName
    }));
  }, [banner.id, screen, isBannerInFeed, banner.newsFeed.dismissed, screenName, track]);
  const bannerTypeMapping = {
    [TaxpayerBannerType.Warning]: InfoBanner.Type.Warning,
    [TaxpayerBannerType.Error]: InfoBanner.Type.Error,
    [TaxpayerBannerType.Success]: InfoBanner.Type.Success,
    [TaxpayerBannerType.Info]: InfoBanner.Type.Info
  };
  const maximumBannerActions = 2;

  const getBannerType = () => {
    return bannerTypeMapping[banner.type];
  };

  const transformBannerAction = action => ({
    text: action.title,
    callback: () => {
      track(clickEvent({
        screen: screenName,
        target: ClickableElement.TaxpayersDac7Banner,
        targetDetails: JSON.stringify({
          button: action.type === 'primary' ? 'finalise' : 'learn',
          id: banner.id
        })
      }));
      navigateToPage(action.link);
    }
  });

  const getBannerActions = () => {
    if (!banner.actions) return [];
    const primaryAction = banner.actions.find(action => action.type === 'primary');
    const secondaryAction = banner.actions.find(action => action.type === 'secondary');

    if (banner.actions.length === maximumBannerActions && primaryAction && secondaryAction) {
      return [transformBannerAction(primaryAction), transformBannerAction(secondaryAction)];
    }

    if (banner.actions.length === 1 && primaryAction) {
      return [transformBannerAction(primaryAction)];
    }

    if (banner.actions.length === 1 && secondaryAction) {
      return [transformBannerAction(secondaryAction)];
    }

    return [];
  };

  const handleBannerDismiss = () => {
    if (!(banner.newsFeed.isDismissible && isBannerInFeed)) return;
    setShow(false);
    dismissTaxpayerRestrictionBanner();
    track(clickEvent({
      screen: screenName,
      target: ClickableElement.TaxpayersDac7Banner,
      targetDetails: JSON.stringify({
        button: 'close',
        id: banner.id
      })
    }));
  };

  const handleModalClose = () => {
    setShowModal(false);
    dismissTaxpayerRestrictionModal();
  };

  const renderModal = () => {
    if (!isBannerInFeed) return null;
    if (!banner.newsFeed.showModal) return null;
    return /*#__PURE__*/_jsx(TaxpayerRestrictionInfoModal, {
      show: showModal,
      isSellingBlocked: banner.id === TaxpayerBannerTrackingId.SellingBlocked,
      screen: screen,
      onClose: handleModalClose
    });
  };

  if (isBannerInFeed && banner.newsFeed.dismissed) return null;
  if (!show) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(InfoBanner, {
      title: banner.title,
      body: banner.body,
      closable: banner.newsFeed.isDismissible && isBannerInFeed,
      styling: InfoBanner.Styling.Tight,
      type: getBannerType(),
      actions: getBannerActions(),
      onClose: handleBannerDismiss,
      testId: "taxpayer-banner"
    }), renderModal()]
  });
};

export default TaxpayerBanner;